'use client';

import { useCallback, useEffect, useRef } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { isAndroidAppWebView, isIosAppWebView } from '@kcd/app-interface';
import { closeCurrentModal } from '@kcd/app-interface/dist/web2app';

import { PRODUCT_TYPE_E_COUPON } from '~/domains/e-coupon';

const HISTORY_STACK_SESSION_STORAGE_KEY = 'history-stack';
const CURRENT_HISTORY_PAGE_INDEX_KEY = 'current-history-index';

type History = {
  key: string;
  href: string;
};

// ============================================
// Storage Utilities
// ============================================
const getHistoryStack = (): History[] => {
  if (typeof window === 'undefined') return [];
  const storageData = sessionStorage.getItem(HISTORY_STACK_SESSION_STORAGE_KEY);
  return (storageData ? JSON.parse(storageData) : []) as History[];
};

const setHistoryStack = (stack: History[]) =>
  sessionStorage.setItem(
    HISTORY_STACK_SESSION_STORAGE_KEY,
    JSON.stringify(stack),
  );

const getCurrentHistoryIndex = (): number => {
  if (typeof window === 'undefined') return 0;
  const storageData = sessionStorage.getItem(CURRENT_HISTORY_PAGE_INDEX_KEY);
  return (storageData ? JSON.parse(storageData) : 0) as number;
};

const setCurrentHistoryIndex = (index: number) =>
  sessionStorage.setItem(CURRENT_HISTORY_PAGE_INDEX_KEY, `${index}`);

export const getHistoryIndexByKey = (key: string): number => {
  const historyIndex = getHistoryStack().findIndex(
    (history) => history.key === key,
  );
  return historyIndex === -1 ? 0 : historyIndex;
};

const getHistoryLength = () => getHistoryStack().length;

const hasHistoryIndex = (key: string) =>
  getHistoryStack().some((history) => history.key === key);

// ============================================
// App Router용 history key 관리
// Page Router는 Next.js가 자동 생성했지만, App Router는 직접 관리 필요
// ============================================
const generateHistoryKey = (): string =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const getOrCreateHistoryKey = (): string => {
  const { state } = window.history;

  if (state?.historyKey) {
    return state.historyKey as string;
  }

  const newKey = generateHistoryKey();
  window.history.replaceState({ ...state, historyKey: newKey }, '');

  return newKey;
};

// ============================================
// Hook
// ============================================
export const useHistoryIndexChangeByRoute = () => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isInitialMount = useRef(true);

  useEffect(() => {
    const updateHistory = () => {
      const key = getOrCreateHistoryKey();
      const { href } = window.location;

      let historyStack = getHistoryStack();
      const currentHistoryIndex = getCurrentHistoryIndex();
      const currentHistory = historyStack[currentHistoryIndex] as
        | History
        | undefined;

      // 새로고침을 수행함
      if (currentHistory?.href === href) {
        return;
      }

      // 이전 히스토리로 돌아옴
      if (hasHistoryIndex(key)) {
        setCurrentHistoryIndex(getHistoryIndexByKey(key));
      } else {
        // 새로운 히스토리가 쌓임
        // 현재 히스토리 이후 히스토리들을 제거
        if (currentHistoryIndex + 1 < getHistoryLength()) {
          historyStack = historyStack.slice(0, currentHistoryIndex + 1);
        }

        historyStack.push({
          key,
          href,
        });

        setHistoryStack(historyStack);
        setCurrentHistoryIndex(historyStack.length - 1);
      }
    };

    if (isInitialMount.current) {
      isInitialMount.current = false;
      updateHistory();
      return;
    }

    updateHistory();
  }, [pathname, searchParams]);

  // popstate 이벤트 처리 (뒤로가기/앞으로가기)
  useEffect(() => {
    const handlePopState = () => {
      const key = getOrCreateHistoryKey();

      if (hasHistoryIndex(key)) {
        setCurrentHistoryIndex(getHistoryIndexByKey(key));
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);
};

/**
 * @description 뒤로가기 버튼 클릭 시 동작하는 함수 (app-router 사용 시에만 사용)
 * */
export const useGoBack = () => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const productType = searchParams?.get('productType');

  return useCallback(() => {
    const key = getOrCreateHistoryKey();
    const historyIndex = getHistoryIndexByKey(key);

    if (!historyIndex || historyIndex === 0) {
      if (isIosAppWebView() || isAndroidAppWebView()) {
        closeCurrentModal({ fallback: () => window.close() });
        return;
      }

      if (productType === PRODUCT_TYPE_E_COUPON) {
        void router.replace('/e-coupon');
      } else {
        void router.replace('/app');
      }
      return;
    }

    router.back();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router, productType]);
};
